import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import PersonAddOutlined from '@mui/icons-material/PersonAddOutlined';
import api from '../services/api';
import { AuthContext } from "../contexts/AuthContext";
import AppShell from '../components/layout/AppShell';
import {
  Button,
  PageHeader,
  PageLoader,
  Alert,
  Card,
  CardHeader,
  CardBody,
  Input,
  Select,
  DataTable,
  ConfirmDialog,
} from '../components/ui';

function SettingsPage() {
  const { token, userId, role } = useContext(AuthContext);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    oldPassword: "",
    newPassword: "",
  });
  const [loading, setLoading] = useState(true);
  const [loadingTooLong, setLoadingTooLong] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState("");
  const [adminSuccessMessage, setAdminSuccessMessage] = useState("");
  const [adminErrorMessage, setAdminErrorMessage] = useState("");
  const [pendingDeleteUser, setPendingDeleteUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(false);
  const [pendingRoles, setPendingRoles] = useState({});
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [newUserData, setNewUserData] = useState({
    username: "",
    email: "",
    password: "",
    role: "user",
  });

  // Fetch user details
  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const response = await api.get(
          `/user/users/${userId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setFormData({
          username: response.data.username || "",
          email: response.data.email || "",
          oldPassword: "",
          newPassword: "",
        });
        setLoading(false);
      } catch (error) {
        setErrorMessage("Failed to fetch user details.");
        setLoading(false);
      }
    };

    fetchUserDetails();
  }, [userId, token]);

  const fetchAllUsers = useCallback(async () => {
    if (role !== 'admin') return;

    setUsersLoading(true);
    setUsersError("");
    try {
      const response = await api.get('/user/users', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (Array.isArray(response.data)) {
        setUsers(response.data);
      } else {
        setUsers([]);
      }
    } catch (error) {
      setUsersError(error.response?.data?.message || 'Failed to fetch users.');
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, [role, token]);

  useEffect(() => {
    if (role === 'admin' && token) {
      fetchAllUsers();
    }
  }, [role, token, fetchAllUsers]);

  // Timeout to show logout suggestion if loading takes too long
  useEffect(() => {
    let timeoutId;
    if (loading) {
      timeoutId = setTimeout(() => {
        setLoadingTooLong(true);
      }, 10000); // Show message after 10 seconds
    } else {
      setLoadingTooLong(false);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [loading]);

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('userId');
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('userId');
    navigate('/login');
  };

  // Handle form input changes
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleNewUserChange = (e) => {
    setNewUserData({ ...newUserData, [e.target.name]: e.target.value });
  };

  const handleRoleSelectionChange = (userIdValue, nextRole) => {
    setPendingRoles((prev) => ({ ...prev, [userIdValue]: nextRole }));
  };

  const handleSaveRole = async (targetUser) => {
    const nextRole = pendingRoles[targetUser._id] || targetUser.role;
    if (nextRole === targetUser.role) return;

    setAdminSuccessMessage("");
    setAdminErrorMessage("");
    try {
      await api.put(
        `/user/users/${targetUser._id}`,
        {
          username: targetUser.username,
          email: targetUser.email,
          role: nextRole,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setUsers((prev) =>
        prev.map((user) =>
          user._id === targetUser._id
            ? { ...user, role: nextRole }
            : user
        )
      );
      setAdminSuccessMessage(`Role updated for ${targetUser.username}.`);
    } catch (error) {
      setAdminErrorMessage(error.response?.data?.message || 'Failed to update role.');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setAdminSuccessMessage("");
    setAdminErrorMessage("");
    setIsCreatingUser(true);

    try {
      await api.post('/user/users', newUserData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setAdminSuccessMessage(`User ${newUserData.username} created successfully.`);
      setNewUserData({ username: "", email: "", password: "", role: "user" });
      fetchAllUsers();
    } catch (error) {
      setAdminErrorMessage(error.response?.data?.message || 'Failed to create user.');
    } finally {
      setIsCreatingUser(false);
    }
  };

  const handleDeleteUser = (targetUser) => {
    if (targetUser._id === userId) {
      setAdminErrorMessage('You cannot delete your own account from this page.');
      return;
    }
    setPendingDeleteUser(targetUser);
  };

  const confirmDeleteUser = async () => {
    if (!pendingDeleteUser) return;

    setAdminSuccessMessage("");
    setAdminErrorMessage("");
    setDeletingUser(true);
    try {
      await api.delete(`/user/users/${pendingDeleteUser._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setUsers((prev) => prev.filter((user) => user._id !== pendingDeleteUser._id));
      setAdminSuccessMessage(`User ${pendingDeleteUser.username} deleted.`);
    } catch (error) {
      setAdminErrorMessage(error.response?.data?.message || 'Failed to delete user.');
    } finally {
      setDeletingUser(false);
      setPendingDeleteUser(null);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
  
    setIsSubmitting(true);
    setSuccessMessage("");
    setErrorMessage("");
    setIsSubmitted(false);
  
    try {
      const updatedData = {
        username: formData.username,
        email: formData.email,
      };
  
      // If user wants to update password, first validate the old password
      if (formData.newPassword) {
        if (!formData.oldPassword) {
          setErrorMessage("Please enter your old password.");
          setIsSubmitting(false);
          return;
        }
  
        // Verify old password before allowing new password change
        try {
          const verifyResponse = await api.post(
            `/user/verify-password`,
            { oldPassword: formData.oldPassword },
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
  
          if (!verifyResponse.data.success) {
            setErrorMessage("Old password is incorrect.");
            setIsSubmitting(false);
            return;
          }
        } catch (error) {
          if (error.response && error.response.status === 401) {
            setErrorMessage("Old password is incorrect.");
          } else {
            setErrorMessage("Failed to verify old password. Please try again.");
          }
          setIsSubmitting(false);
          return;
        }
  
        // If verification passes, update password
        updatedData.password = formData.newPassword;
      }
  
      await api.put(
        `/user/users/${userId}`,
        updatedData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
  
      setSuccessMessage("Profile updated successfully!");
      setIsSubmitted(true);
    } catch (error) {
      setErrorMessage("Failed to update profile. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const userColumns = [
    { key: 'username', header: 'Username', primary: true },
    { key: 'email', header: 'Email' },
    {
      key: 'role',
      header: 'Role',
      render: (userRow) => (
        <Select
          aria-label={`Role for ${userRow.username}`}
          value={pendingRoles[userRow._id] || userRow.role}
          onChange={(e) => handleRoleSelectionChange(userRow._id, e.target.value)}
          className="w-32"
        >
          <option value="user">User</option>
          <option value="admin">Admin</option>
        </Select>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (userRow) => {
        const roleChanged = (pendingRoles[userRow._id] || userRow.role) !== userRow.role;
        const isCurrentUser = userRow._id === userId;

        return (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => handleSaveRole(userRow)} disabled={!roleChanged}>
              Save role
            </Button>
            <Button
              variant="danger-soft"
              size="sm"
              icon={DeleteOutline}
              onClick={() => handleDeleteUser(userRow)}
              disabled={isCurrentUser}
              title={isCurrentUser ? 'You cannot delete yourself' : undefined}
            >
              Delete
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader title="Settings" subtitle="Your profile and account access" backTo="/" />

      <AppShell>
        {loading ? (
          <>
            <PageLoader label="Loading settings…" />
            {loadingTooLong && (
              <Alert
                variant="warning"
                title="Taking longer than usual"
                className="mx-auto max-w-md"
                action={
                  <Button variant="secondary" size="sm" onClick={handleLogout}>
                    Log out and retry
                  </Button>
                }
              >
                Your session may have expired.
              </Alert>
            )}
          </>
        ) : (
          <div className="space-y-8">
            <Card>
              <CardHeader title="Profile" subtitle="Your account details and password" />
              <CardBody>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid gap-6 lg:grid-cols-2 lg:gap-8">
                    <fieldset className="min-w-0 space-y-4">
                      <legend className="mb-4 text-sm font-semibold text-foreground">
                        Account details
                      </legend>
                      <Input
                        label="Username"
                        type="text"
                        name="username"
                        value={formData.username}
                        onChange={handleChange}
                        required
                      />
                      <Input
                        label="Email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        autoComplete="email"
                        required
                      />
                    </fieldset>

                    <fieldset className="min-w-0 space-y-4 border-t border-border pt-4 lg:border-l lg:border-t-0 lg:pl-8 lg:pt-0">
                      <legend className="mb-4 text-sm font-semibold text-foreground">
                        Change password
                      </legend>
                      <Input
                        label="Current password"
                        type="password"
                        name="oldPassword"
                        value={formData.oldPassword}
                        onChange={handleChange}
                        autoComplete="current-password"
                        required={formData.newPassword.length > 0}
                      />

                      <Input
                        label="New password"
                        type="password"
                        name="newPassword"
                        value={formData.newPassword}
                        onChange={handleChange}
                        autoComplete="new-password"
                      />
                      <p className="text-sm text-muted-fg">
                        Leave both password fields blank to keep your current password.
                      </p>
                    </fieldset>
                  </div>

                  {errorMessage && <Alert variant="error">{errorMessage}</Alert>}
                  {successMessage && <Alert variant="success">{successMessage}</Alert>}

                  <div className="flex flex-col-reverse gap-3 border-t border-border pt-4 sm:flex-row sm:justify-end">
                    <Button type="button" variant="secondary" size="lg" onClick={() => navigate('/')}>
                      Cancel
                    </Button>
                    <Button type="submit" size="lg" loading={isSubmitting} disabled={isSubmitted}>
                      {isSubmitted ? 'Saved' : 'Save changes'}
                    </Button>
                  </div>
                </form>
              </CardBody>
            </Card>

            {role === 'admin' && (
              <Card>
                <CardHeader title="User management" subtitle="Create accounts and change roles" />
                <CardBody>
                  <form
                    onSubmit={handleCreateUser}
                    autoComplete="off"
                    className="mb-6 rounded-lg border border-border bg-muted/40 p-4"
                  >
                    <h3 className="mb-4 text-xs font-semibold uppercase tracking-wide text-muted-fg">
                      Add a user
                    </h3>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <Input
                        label="Username"
                        type="text"
                        name="username"
                        value={newUserData.username}
                        onChange={handleNewUserChange}
                        autoComplete="off"
                        required
                      />
                      <Input
                        label="Email"
                        type="email"
                        name="email"
                        value={newUserData.email}
                        onChange={handleNewUserChange}
                        autoComplete="off"
                        required
                      />
                      <Input
                        label="Temporary password"
                        type="password"
                        name="password"
                        value={newUserData.password}
                        onChange={handleNewUserChange}
                        autoComplete="section-add-user new-password"
                        hint="The user should change this after first sign-in."
                        required
                      />
                      <Select
                        label="Role"
                        name="role"
                        value={newUserData.role}
                        onChange={handleNewUserChange}
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                      </Select>
                    </div>

                    <div className="mt-4 flex justify-end">
                      <Button type="submit" icon={PersonAddOutlined} loading={isCreatingUser}>
                        Add user
                      </Button>
                    </div>
                  </form>

                  {adminErrorMessage && (
                    <Alert variant="error" className="mb-4">
                      {adminErrorMessage}
                    </Alert>
                  )}
                  {adminSuccessMessage && (
                    <Alert variant="success" className="mb-4">
                      {adminSuccessMessage}
                    </Alert>
                  )}
                  {usersError && (
                    <Alert variant="error" className="mb-4">
                      {usersError}
                    </Alert>
                  )}

                  <DataTable
                    columns={userColumns}
                    rows={users}
                    loading={usersLoading}
                    emptyTitle="No users"
                    emptyDescription="No accounts have been created yet."
                  />
                </CardBody>
              </Card>
            )}
          </div>
        )}
      </AppShell>

      <ConfirmDialog
        isOpen={Boolean(pendingDeleteUser)}
        onClose={() => setPendingDeleteUser(null)}
        onConfirm={confirmDeleteUser}
        loading={deletingUser}
        title="Delete user"
        message={`This permanently removes the account for ${pendingDeleteUser?.username}. This cannot be undone.`}
        confirmLabel="Delete user"
      />
    </>
  );
}

export default SettingsPage;